import { Box, Button, Popover, Stack } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import { getSelectorAttribute, vyTableSelectors } from '@vy/app-selectors';
import { isEmptyArray, isNotNullOrUndefined } from '@vy/utils';
import React, { useCallback, useEffect } from 'react';
import { useTableFilter } from '../hooks/use-table-filter';
import type { FilterField, FilterLine as FilterLineType, FilterValue } from '../types';
import { hasIncompleteFilter } from '../vyTableFilterUtils';
import { FilterLine } from './FilterLine';

type FilterPopoverProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  fields: FilterField[];
  onFiltersChange: (filters: FilterLineType[]) => void;
  defaultFilters?: FilterLineType[];
};

export const FilterPopover: React.FC<FilterPopoverProps> = React.memo(
  ({ anchorEl, open, onClose, fields, onFiltersChange, defaultFilters = [] }) => {
    const initialFilters = defaultFilters;

    const {
      filterLines = [],
      addFilter,
      deleteFilter,
      updateFilter,
      resetFilters,
      getAvailableFields,
      getFieldOperators,
      getFieldInputType,
      getFieldOptions,
      canAddFilter,
    } = useTableFilter(fields, initialFilters);

    useEffect(() => {
      if (open && (filterLines?.length ?? 0) === 0) {
        addFilter();
      }
    }, [open]);

    const handleClose = useCallback(() => {
      onClose();
    }, [onClose]);

    const handleAddFilter = useCallback(() => {
      addFilter();
    }, [addFilter]);

    const handleClearFilters = useCallback(() => {
      resetFilters();
      onFiltersChange([]);
    }, [resetFilters, onFiltersChange]);

    const handleDeleteFilterLine = useCallback(
      (filterId: string) => {
        const isOnlyFilter = (filterLines ?? []).length === 1;

        if (isOnlyFilter) {
          resetFilters();
          onFiltersChange([]);
          onClose();
          return;
        }

        deleteFilter(filterId);

        const remainingFilters = filterLines?.filter((filter) => filter.id !== filterId) ?? [];
        const validFilters = remainingFilters.filter((filter) => !hasIncompleteFilter(filter));
        onFiltersChange(validFilters);
      },
      [deleteFilter, filterLines, onFiltersChange, onClose, resetFilters],
    );

    const handleUpdateFilter = useCallback(
      (filterId: string, field: keyof FilterLineType, value: FilterValue) => {
        updateFilter(filterId, field, value);

        if (field === 'field') {
          updateFilter(filterId, 'operator', '');
          updateFilter(filterId, 'value', []);
        }

        const filterToUpdate = filterLines?.find((filterLine) => filterLine.id === filterId);

        if (isNotNullOrUndefined(filterToUpdate)) {
          const updatedFilter = { ...filterToUpdate, [field]: value };

          if (field === 'field') {
            updatedFilter.operator = '';
            updatedFilter.value = [];
          }

          const shouldUpdateFilters = field === 'value' || !hasIncompleteFilter(updatedFilter);

          if (shouldUpdateFilters) {
            const allValidFilters = (filterLines ?? [])
              .map((filter) => (filter.id === filterId ? updatedFilter : filter))
              .filter((filter) => !hasIncompleteFilter(filter));
            onFiltersChange(allValidFilters);
          }
        }
      },
      [updateFilter, filterLines, onFiltersChange, getFieldInputType, handleDeleteFilterLine],
    );

    const hasNoFilters = isEmptyArray(filterLines ?? []);

    return (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        {...getSelectorAttribute(vyTableSelectors.filterPopover)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              padding: 2,
              width: 710,
              minHeight: 140,
              borderRadius: '8px',
              boxShadow: '0px 8px 16px 0px #0C124514',
              border: '1px solid #F0F4F6',
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'column',
              gap: 2,
            },
          },
        }}
      >
        <Stack spacing={2}>
          <Stack spacing={1}>
            {filterLines.map((filter, index) => (
              <FilterLine
                key={filter.id}
                filter={filter}
                availableFields={getAvailableFields(filter.id)}
                operators={getFieldOperators(filter.field)}
                inputType={getFieldInputType(filter.field)}
                options={getFieldOptions(filter.field)}
                onUpdateFilter={(field, value) => handleUpdateFilter(filter.id, field, value)}
                onDeleteFilter={() => handleDeleteFilterLine(filter.id)}
                isFirstLine={index === 0}
              />
            ))}
          </Stack>
        </Stack>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
          <Button
            variant="text"
            onClick={handleAddFilter}
            disabled={!canAddFilter}
            startIcon={<VyIcon style={{ transform: 'scale(0.8)' }} icon="PlusMedium" />}
            sx={{
              fontSize: '14px',
              textTransform: 'none',
              color: '#142232',
              borderRadius: '4px',
              backgroundColor: '#E8EBF2',
              '&:hover': {
                backgroundColor: '#E0E0E0',
              },
              '&:disabled': {
                backgroundColor: '#E8EBF2',
                opacity: 0.6,
                color: '#666',
              },
            }}
          >
            Add filter
          </Button>

          {!hasNoFilters && (
            <Button
              variant="text"
              onClick={handleClearFilters}
              sx={{
                fontSize: '14px',
                textTransform: 'none',
                color: '#666',
              }}
            >
              Clear filters
            </Button>
          )}
        </Box>
      </Popover>
    );
  },
);
